<template>
  <Empathize
    :animation="empathizeAnimation"
    class="x-z-40 x-rounded-md x-bg-neutral-0 desktop:x-w-full"
    :events-to-close-empathize="isDesktopOrGreater ? desktopCloseEvents : mobileCloseEvents"
  >
    <component
      :is="isDesktopOrGreater ? 'div' : 'BaseScroll'"
      class="x-h-full x-rounded-md"
      :class="{ 'x-bg-neutral-0': showEmpathize }"
    >
      <div
        v-if="showEmpathize || showIdentifierResults"
        class="x-layout-item x-pb-16 desktop:x-block desktop:x-pl-16 desktop:x-pt-16"
        :class="{ 'x-mb-40 x-border-b x-border-neutral-10': !x.query.search && isTabletOrLess }"
      >
        <BaseKeyboardNavigation
          class="x-flex x-flex-col desktop:x-grid desktop:x-grid-cols-12 desktop:x-items-start desktop:x-gap-24"
          :navigation-hijacker="navigationHijacker"
        >
          <PredictiveIdentifierResults
            v-if="showIdentifierResults"
            :animation="suggestionsAnimation"
          />

          <div
            v-else-if="showEmpathize"
            class="x-flex x-flex-col x-gap-20 x-pl-8 desktop:x-col-span-5 desktop:x-pl-0"
            :class="x.query.searchBox ? 'desktop:x-gap-4' : 'desktop:x-gap-16'"
          >
            <div v-if="showHistoryQueries" class="x-flex x-flex-col x-gap-12">
              <div v-if="!x.query.searchBox" class="x-flex x-items-center">
                <h1 class="x-title4 x-title4-sm x-flex-1 x-uppercase x-text-neutral-75">
                  {{ $t('historyQueries.title') }}
                </h1>

                <ClearHistoryQueries
                  class="x-button-neutral x-button-sm x-button-tight x-title4 x-title4-sm x-px-8 x-text-neutral-75"
                >
                  <TrashIcon v-if="isTabletOrLess" class="x-icon-lg" />
                  <span v-else>{{ $t('historyQueries.clear') }}</span>
                </ClearHistoryQueries>
              </div>

              <HistoryQueries
                :animation="suggestionsAnimation"
                :max-items-to-render="x.query.searchBox ? 2 : 4"
                :suggestion-item-class="suggestionItemClass"
                class="-x-mr-2 x-flex x-gap-8 desktop:!x-flex-col desktop:x-gap-4"
                :class="{ '!x-flex-row !x-flex-wrap': !x.query.searchBox }"
              >
                <template #suggestion="{ suggestion }">
                  <HistoryQuery
                    class="x-suggestion-group-md hover:x-no-underline"
                    :suggestion="suggestion"
                    :remove-button-class="removeButtonClass"
                    :suggestion-class="`x-suggestion x-suggestion-md x-text-neutral-90 ${suggestionClass}`"
                  >
                    <template #default="{ query }">
                      <HistoryIcon class="x-icon-lg desktop:x-icon-md" />
                      <div
                        class="x-group x-text2 x-text2-lg x-flex x-flex-col x-gap-2 desktop:x-font-bold"
                      >
                        <Highlight
                          :text="suggestion.query"
                          :highlight="query"
                          class="group-hover:x-underline"
                        />
                        <HistoryQueryFilters
                          class="x-w-192 desktop:x-w-128"
                          :filters-list="suggestion.selectedFilters"
                        />
                      </div>
                    </template>

                    <template #remove-button-content>
                      <span
                        :aria-label="
                          $t('historyQueries.removeLabel', { suggestion: suggestion.query })
                        "
                      >
                        <CrossTinyIcon class="x-icon-lg desktop:x-icon-md" />
                      </span>
                    </template>
                  </HistoryQuery>
                </template>
              </HistoryQueries>
            </div>

            <PredictiveQuerySuggestions
              v-if="showQuerySuggestions"
              :animation="suggestionsAnimation"
            />

            <PredictiveNextQueries v-if="showNextQueries" :animation="suggestionsAnimation" />

            <PredictivePopularSearches
              v-if="showPopularSearches"
              :animation="suggestionsAnimation"
            />
          </div>

          <SlidingRecommendations
            v-if="isDesktopOrGreater && !x.query.searchBox"
            class="x-col-span-7"
          />
        </BaseKeyboardNavigation>
      </div>
      <SlidingRecommendations v-if="isTabletOrLess && !x.query.searchBox" />
    </component>
  </Empathize>
</template>

<script setup lang="ts">
import type { XEventsTypes } from '@empathyco/x-components'
import {
  AnimateScale,
  BaseKeyboardNavigation,
  CrossTinyIcon,
  Highlight,
  HistoryIcon,
  StaggeredFadeAndSlide,
  TrashIcon,
  use$x,
} from '@empathyco/x-components'
import { Empathize } from '@empathyco/x-components/empathize'
import {
  ClearHistoryQueries,
  HistoryQueries,
  HistoryQuery,
} from '@empathyco/x-components/history-queries'
import { computed } from 'vue'
import { useDevice } from '../../composables/use-device.composable'
import { usePredictiveHelpers } from '../../composables/use-predictive-helpers.composable'
import HistoryQueryFilters from '../history-query-filters.vue'
import PredictiveIdentifierResults from './predictive-identifier-results.vue'
import PredictiveNextQueries from './predictive-next-queries.vue'
import PredictivePopularSearches from './predictive-popular-searches.vue'
import PredictiveQuerySuggestions from './predictive-query-suggestions.vue'
import SlidingRecommendations from './sliding-recommendations.vue'

const empathizeAnimation = AnimateScale
const suggestionsAnimation = StaggeredFadeAndSlide as any
const { isDesktopOrGreater, isTabletOrLess } = useDevice()
const x = use$x()

const {
  navigationHijacker,
  showIdentifierResults,
  showHistoryQueries,
  showQuerySuggestions,
  showNextQueries,
  showPopularSearches,
  showEmpathize,
} = usePredictiveHelpers()

const mobileCloseEvents: Array<keyof XEventsTypes> = [
  'UserClosedEmpathize',
  'UserSelectedASuggestion',
  'UserPressedEnterKey',
]
const desktopCloseEvents: Array<keyof XEventsTypes> = [...mobileCloseEvents, 'UserBlurredSearchBox']

const suggestionItemClass = computed(() => (isDesktopOrGreater.value ? 'x-w-full' : ''))
const removeButtonClass = computed(() => (!isDesktopOrGreater.value ? 'x-hidden' : ''))
const suggestionClass = computed(() =>
  !isDesktopOrGreater.value && !x.query.searchBox
    ? 'x-px-12 x-border-1 x-border-neutral-25 x-items-baseline x-rounded-full'
    : '',
)
</script>
